/**
 * Product info tabs — switches tab panels for PDP product information.
 */
class ProductInfoTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activateTab(tab));
      tab.addEventListener('keydown', (event) => this.handleKeydown(event));
    });

    // Start the row on the open tab once layout has settled
    const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (active) {
      if (document.readyState === 'complete') {
        this.centreTab(active, 'auto');
      } else {
        window.addEventListener('load', () => this.centreTab(active, 'auto'), { once: true });
      }
    }
  }

  /**
   * On mobile the tab row scrolls sideways. Glide it so the chosen tab sits
   * in the middle with the tabs either side in view (same idea as
   * pdp-option-centre.js for the size cards). Rows that fit are left alone.
   * @param {HTMLElement} tab
   * @param {ScrollBehavior} behavior
   */
  centreTab(tab, behavior) {
    const row = tab.closest('[role="tablist"]');
    if (!row || !row.clientWidth || row.scrollWidth <= row.clientWidth) return;

    const rowBox = row.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    const offset = tabBox.left + tabBox.width / 2 - (rowBox.left + rowBox.width / 2);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    row.scrollBy({ left: offset, behavior: reduceMotion ? 'auto' : behavior });
  }

  /**
   * @param {HTMLElement} tab
   */
  activateTab(tab) {
    const tabKey = tab.getAttribute('data-tab');

    this.tabs.forEach((item) => {
      const isActive = item === tab;
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      item.setAttribute('tabindex', isActive ? '0' : '-1');
      item.classList.toggle('is-active', isActive);
    });

    this.panels.forEach((panel) => {
      const isActive = panel.getAttribute('data-tab-panel') === tabKey;
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    this.centreTab(tab, 'smooth');
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = this.tabs[nextIndex];
    nextTab.focus();
    this.activateTab(nextTab);
  }
}

if (!customElements.get('product-info-tabs')) {
  customElements.define('product-info-tabs', ProductInfoTabs);
}
